import type { CommentsChannelNav } from '~publish/components/CommentsSidebar/useCommentsChannels'

import type { GetChannelResult } from './hooks/comments/useGetChannel'
import type {
  CommentStatus,
  CommentEngagementStatusTypes,
} from './graphqlEnums'

// The two Community channel producers: the sidebar list (`CommentsChannelNav`)
// and the single-channel view (`GetChannelResult`, from `useGetChannel`), each
// a gql.tada selection type.
export type ChannelType = CommentsChannelNav | GetChannelResult

export type SavedReplyId = string
export type { SavedReply } from './hooks/savedReplies/useSavedRepliesQuery'

export type CommunityTab = 'comments' | 'mentions' | 'dms'
export const DEFAULT_TAB: CommunityTab = 'comments'

// Tab labels are not derived from the tab value: `dms` reads as "Dms" when
// title-cased, and the surface is called Messages in the product.
export const TAB_LABELS = {
  comments: 'Comments',
  mentions: 'Mentions',
  dms: 'Messages',
} as const satisfies Record<CommunityTab, string>

// Extend the GraphQL CommentStatus with 'all' for UI purposes
export type CommentStatusWithAll = CommentStatus | 'all'

// Type-safe comment status constants
export const COMMENT_STATUS = {
  All: 'all',
  New: 'new',
  Replied: 'replied',
  Unreplied: 'unreplied',
  Dismissed: 'dismissed',
} as const satisfies Record<string, CommentStatusWithAll>

// Extend GraphQL CommentEngagementStatusTypes with 'idle' for UI purposes
// 'published' state persists to maintain resolved styling for locally replied comments
export type ReplyFormStatus = CommentEngagementStatusTypes | 'idle'

// Reply form error types
export type ReplyErrorType =
  | 'not_found'
  | 'validation_error'
  | 'unauthorized'
  | 'api_error'
  | 'unknown'

// Type-safe error type constants
export const REPLY_ERROR_TYPE = {
  NotFound: 'not_found',
  ValidationError: 'validation_error',
  Unauthorized: 'unauthorized',
  ApiError: 'api_error',
  Unknown: 'unknown',
} as const satisfies Record<string, ReplyErrorType>
